/* REACT */
import React, { useEffect, memo, useCallback } from 'react'
import { useState } from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import cookie from 'react-cookies'

/* COMPONENTS */
import ChatSection from './components/ChatSection'
import MayaIntentComponent from './components/MayaIntentComponent'
import { Spinner } from '../../../components/ui'
import FeedbackModal from '../../../components/modals/feedback/FeedbackModal'
import SaveChatModal from '../../../components/modals/saveChat/SaveChatModal'
import API from '../../../utils/api'

/* STORE */
import { setIsGridMode } from '../../../store/maya/actions'
import { setShowSendChatModal } from '../../../store/general/actions'

/* STYLES */
import {
  AiPlannerWrapper,
  MayaMobileTabs,
  MayaMobileTabsWrapper,
  MayaPageWrapper,
  AiPlannerContainer,
  NotificationDot,
  StyledToastContainer,
} from './components/style'

/* UTILS/CONSTANTS/IMAGES-ASSETS */
import { size, CLIENT_STYLINGS } from '../../../utils/constants'
import {
  createLoadingSelector,
  Analytics,
  getTranslations,
} from '../../../utils'
import { FETCH_CHAT } from '../../../store/maya/constants.js'

// Create a completely separate memoized tabs component
const MobileTabs = memo(({ isGridMode, setIsGridMode, intent, language }) => {
  const handleChatClick = useCallback(() => {
    Analytics.getInstance().sendEvent('maya_click_button', {
      button_type: 'tab',
      button_text: 'Chat',
      button_subtype: 'to_chat',
    })
    setIsGridMode(false)
  }, [setIsGridMode])

  const handleRecommendationsClick = useCallback(() => {
    Analytics.getInstance().sendEvent('maya_click_button', {
      button_type: 'tab',
      button_text: `${
        intent?.type ? getTranslations(`${intent.type}_tab`) : ''
      }`,
      button_subtype: 'to_recommendations',
    })
    setIsGridMode(true)
  }, [intent?.type, language, setIsGridMode])

  return (
    <MayaMobileTabsWrapper>
      <MayaMobileTabs
        active={!isGridMode}
        onClick={handleChatClick}
        $showSecondTab={false}
      >
        Chat
      </MayaMobileTabs>
      <MayaMobileTabs
        active={isGridMode}
        onClick={handleRecommendationsClick}
        $showSecondTab={false}
      >
        {intent?.type
          ? getTranslations(`${intent.type}_tab`)
          : getTranslations(`qa_tab`)}
        {intent?.type && <NotificationDot />}
      </MayaMobileTabs>
    </MayaMobileTabsWrapper>
  )
})

// Add memoized chat section
const MemoizedChatSection = memo(ChatSection, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.messages === nextProps.messages &&
    prevProps.isMayaResponding === nextProps.isMayaResponding &&
    prevProps.showFeedbackModal === nextProps.showFeedbackModal &&
    prevProps.isLessThanLaptop === nextProps.isLessThanLaptop &&
    prevProps.isHorizontal === nextProps.isHorizontal
  )
})

const FullPage = ({
  intent,
  language,
  isFetchingChat,
  setIsGridMode,
  isGridMode,
  organizationSlug,
  showFeedbackModal,
  slug,
  showSendChatModal,
  setShowSendChatModal,
}) => {
  const [isFullPlanMode, setIsFullPlanMode] = useState(false)

  const isLessThanLaptop = useMediaQuery({
    query: `(max-width: ${size.laptop})`,
  })
  const isHorizontal = useMediaQuery({ query: '(orientation: landscape)' })

  useEffect(() => {
    // HOOK FOR CHECKING WHICH TAB TO SHOW INITIALLY ON MOBILE
    if (cookie.load(`maya_is_grid_mode_${process.env.REACT_APP_API_ENV}`)) {
      setIsGridMode(true)
    } else {
      setIsGridMode(false)
    }
  }, [])

  return (
    <>
      {isLessThanLaptop && (
        <MobileTabs
          isGridMode={isGridMode}
          setIsGridMode={setIsGridMode}
          intent={intent}
          language={language}
        />
      )}
      <MayaPageWrapper id='maya-page-wrapper'>
        <AiPlannerWrapper id='ai-planner-wrapper'>
          <StyledToastContainer hideProgressBar={true} />
          <AiPlannerContainer>
            {/* FETCHING CHAT - SHOW SPINNER */}
            {isFetchingChat ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor:
                    CLIENT_STYLINGS[organizationSlug].COLOR.BACKGROUND,
                  backgroundColor:
                    CLIENT_STYLINGS[organizationSlug].COLOR.BACKGROUND,
                  opacity: '0.8',
                  zIndex: '100',
                  position: 'absolute',
                }}
              >
                <Spinner
                  isBasic={false}
                  interval={10000}
                  messages={getTranslations('fetching_chat')}
                />
              </div>
            ) : null}

            {isLessThanLaptop ? (
              <>
                {!isGridMode ? (
                  <MemoizedChatSection
                    isLessThanLaptop={isLessThanLaptop}
                    isHorizontal={isHorizontal}
                    isFullPlanMode={isFullPlanMode}
                    setTravelPlanView={() => {
                      setIsGridMode(true)
                    }}
                    language={language}
                  />
                ) : (
                  <MayaIntentComponent
                    isLessThanLaptop={isLessThanLaptop}
                    language={language}
                  />
                )}
              </>
            ) : (
              <>
                <MemoizedChatSection
                  isFullPlanMode={isFullPlanMode}
                  isLessThanLaptop={isLessThanLaptop}
                  isHorizontal={isHorizontal}
                  language={language}
                />

                <MayaIntentComponent
                  isLessThanLaptop={isLessThanLaptop}
                  language={language}
                />
              </>
            )}
          </AiPlannerContainer>
        </AiPlannerWrapper>
        <SaveChatModal
          isOpen={showSendChatModal}
          variant='dialog'
          onClose={() => setShowSendChatModal(false)}
          onSubmit={({ email }) =>
            API.postRequest('/mail/send-chat', {
              slug,
              email,
            })
          }
        />
      </MayaPageWrapper>
    </>
  )
}

const loadingSelector = createLoadingSelector([FETCH_CHAT])

const mapStateToProps = state => ({
  intent: state?.maya?.intent,
  fullPlan: state?.maya?.fullPlan,
  isFetchingChat: loadingSelector(state.loading),
  isGridMode: state?.maya?.isGridMode,
  organizationSlug: state?.general?.organizationSlug,
  showFeedbackModal: state?.maya?.showFeedbackModal,
  slug: state?.maya?.slug,
  showSendChatModal: state?.general?.showSendChatModal,
})

const mapDispatchToProps = dispatch => ({
  setIsGridMode: data => dispatch(setIsGridMode(data)),
  setShowSendChatModal: isOpen => dispatch(setShowSendChatModal(isOpen)),
})

export default connect(mapStateToProps, mapDispatchToProps)(FullPage)
